import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { Sparkles } from "lucide-react";

/**
 * StateCoverageMap
 * -----------------
 * Small US "tilemap" — one square per state, laid out in a rough geographic
 * grid. States that have at least one verified business light up in gold; the
 * rest sit as pale ghost tiles so the shape still reads as a map of the US.
 *
 * Data source: `GET /api/businesses/states` → `[{state: "GA", count: 12}, ...]`
 *
 * The tilemap is intentionally schematic (not pixel-accurate) — it's meant
 * to communicate "we're already in N states" at a glance without shipping a
 * 200KB SVG map. Every lit tile links straight to the filtered directory.
 */

// [state_code, col, row] on a 12-col × 7-row grid for the contiguous 48.
// AK and HI are rendered separately as bottom-left inset tiles (like a real US map).
const STATES = [
  ["ME", 11, 0],
  ["VT", 10, 1], ["NH", 11, 1],
  ["WA", 0, 2], ["ID", 1, 2], ["MT", 2, 2], ["ND", 3, 2], ["MN", 4, 2],
  ["WI", 5, 2], ["MI", 7, 2], ["NY", 9, 2], ["MA", 10, 2],
  ["OR", 0, 3], ["NV", 1, 3], ["WY", 2, 3], ["SD", 3, 3], ["IA", 4, 3],
  ["IL", 5, 3], ["IN", 6, 3], ["OH", 7, 3], ["PA", 8, 3], ["NJ", 9, 3],
  ["CT", 10, 3], ["RI", 11, 3],
  ["CA", 0, 4], ["UT", 1, 4], ["CO", 2, 4], ["NE", 3, 4],
  ["MO", 4, 4], ["KY", 6, 4], ["WV", 7, 4], ["VA", 8, 4], ["MD", 9, 4],
  ["DE", 10, 4],
  ["AZ", 1, 5], ["NM", 2, 5], ["KS", 3, 5], ["AR", 4, 5], ["TN", 5, 5],
  ["NC", 6, 5], ["DC", 8, 5],
  ["OK", 3, 6], ["LA", 4, 6], ["MS", 5, 6], ["AL", 6, 6],
  ["GA", 7, 6], ["SC", 8, 6],
  ["TX", 3, 7], ["FL", 8, 7],
];

// Alaska & Hawaii sit as inset tiles below the main map (the way real US maps
// show them). Rendered in their own row so they don't get lost in the corner.
const INSET_STATES = ["AK", "HI"];

export default function StateCoverageMap() {
  const [coverage, setCoverage] = useState({}); // { GA: 12, NY: 5, ... }
  const [stats, setStats] = useState({ verified: null, total: null, today: null });
  const [hoveredState, setHoveredState] = useState(null);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/businesses/states`),
      axios.get(`${API}/community/stats`),
    ])
      .then(([statesRes, statsRes]) => {
        const rows = statesRes.data?.states || [];
        const cov = {};
        rows.forEach((r) => { cov[r.state] = r.count; });
        setCoverage(cov);
        const s = statsRes.data || {};
        setStats({
          verified: s.verified_businesses ?? null,
          total: s.total_businesses ?? null,
          today: s.new_verified_today ?? 0,
        });
      })
      .catch(() => {});
  }, []);

  const litStatesCount = Object.keys(coverage).filter((s) => (coverage[s] || 0) > 0).length;

  return (
    <section
      className="bg-tbc-navy text-white overflow-hidden"
      data-testid="state-coverage-map"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 grid lg:grid-cols-5 gap-10 items-center">
        {/* Left copy */}
        <div className="lg:col-span-2">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-tbc-gold/15 text-tbc-gold text-xs tracking-[0.25em] rounded-full mb-4">
            <Sparkles className="w-3.5 h-3.5" />
            NATIONWIDE
          </div>
          <h2 className="font-heading text-3xl lg:text-4xl leading-tight">
            Lighting up the
            <br />
            <span className="text-tbc-gold">country in gold.</span>
          </h2>
          <p className="text-white/70 font-body mt-4 max-w-md">
            Every gold tile is a state where a verified Black-owned business
            calls home. <strong className="text-white">Click any state</strong> to browse the community there — or
            be the first to plant a flag if it&apos;s not lit yet.
          </p>

          <div className="grid grid-cols-2 gap-4 mt-6 max-w-md">
            <div>
              <p className="font-heading text-4xl text-tbc-gold" data-testid="coverage-verified-count">
                {stats.verified ?? "—"}
              </p>
              <p className="text-xs text-white/60 uppercase tracking-widest">
                Verified &amp; counting
              </p>
            </div>
            <div>
              <p className="font-heading text-4xl text-tbc-gold" data-testid="coverage-total-count">
                {stats.total ?? "—"}
              </p>
              <p className="text-xs text-white/60 uppercase tracking-widest">
                Total businesses
              </p>
            </div>
            <div>
              <p className="font-heading text-4xl text-tbc-gold" data-testid="coverage-today-count">
                {stats.today ?? 0}
              </p>
              <p className="text-xs text-white/60 uppercase tracking-widest">
                Verified today
              </p>
            </div>
            <div>
              <p className="font-heading text-4xl text-tbc-gold" data-testid="coverage-states-count">
                {litStatesCount || "—"}
              </p>
              <p className="text-xs text-white/60 uppercase tracking-widest">
                States lit up
              </p>
            </div>
          </div>

          {/* Legend */}
          <div className="flex items-center gap-4 mt-6 text-xs text-white/60">
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-sm bg-tbc-gold shadow-[0_0_8px_rgba(214,175,54,0.6)]" />
              1+ business
            </div>
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-sm bg-white/10 border border-white/20" />
              Not yet
            </div>
          </div>
        </div>

        {/* Right map */}
        <div className="lg:col-span-3">
          <div className="w-full max-w-2xl mx-auto">
            {/* Contiguous 48 — 12-col × 8-row grid */}
            <div
              className="relative grid gap-1.5"
              style={{
                gridTemplateColumns: "repeat(12, minmax(0, 1fr))",
                gridTemplateRows: "repeat(8, minmax(0, 1fr))",
                aspectRatio: "12 / 8",
              }}
            >
              {STATES.map(([code, col, row]) => {
                const count = coverage[code] || 0;
                const lit = count > 0;
                const isHovered = hoveredState === code;

                return (
                  <Link
                    key={code}
                    to={`/directory?state=${code}`}
                    className="contents"
                    data-testid={`state-tile-link-${code}`}
                  >
                    <div
                      className={[
                        "relative rounded flex items-center justify-center transition-all duration-200 border cursor-pointer hover:scale-110 hover:z-10",
                        lit
                          ? "bg-tbc-gold text-tbc-navy border-tbc-gold shadow-[0_0_12px_rgba(214,175,54,0.45)]"
                          : "bg-white/[0.04] text-white/40 border-white/10 hover:bg-white/[0.10] hover:border-white/30",
                      ].join(" ")}
                      style={{ gridColumn: col + 1, gridRow: row + 1 }}
                      onMouseEnter={() => setHoveredState(code)}
                      onMouseLeave={() => setHoveredState(null)}
                      data-testid={`state-tile-${code}`}
                      data-lit={lit ? "true" : "false"}
                    >
                      <span
                        className={`font-heading text-[10px] sm:text-xs font-bold ${
                          lit ? "text-tbc-navy" : "text-white/60"
                        }`}
                      >
                        {code}
                      </span>
                      {isHovered && (
                        <div className="absolute -top-9 left-1/2 -translate-x-1/2 px-2 py-1 bg-white text-tbc-navy text-[10px] rounded whitespace-nowrap shadow-lg font-body z-20">
                          {lit ? `${count} ${count === 1 ? "business" : "businesses"}` : "Browse →"}
                        </div>
                      )}
                    </div>
                  </Link>
                );
              })}
            </div>

            {/* Alaska + Hawaii insets — real US maps show them separately */}
            <div className="mt-4 pt-4 border-t border-white/10 flex items-center gap-3">
              <span className="text-[10px] uppercase tracking-widest text-white/40">
                Also included
              </span>
              <div className="flex gap-2">
                {INSET_STATES.map((code) => {
                  const count = coverage[code] || 0;
                  const lit = count > 0;
                  const isHovered = hoveredState === code;
                  return (
                    <Link
                      key={code}
                      to={`/directory?state=${code}`}
                      data-testid={`state-tile-link-${code}`}
                    >
                      <div
                        className={[
                          "relative w-12 h-9 rounded flex items-center justify-center transition-all duration-200 border cursor-pointer hover:scale-110 hover:z-10",
                          lit
                            ? "bg-tbc-gold text-tbc-navy border-tbc-gold shadow-[0_0_12px_rgba(214,175,54,0.45)]"
                            : "bg-white/[0.04] text-white/40 border-white/10 hover:bg-white/[0.10] hover:border-white/30",
                        ].join(" ")}
                        onMouseEnter={() => setHoveredState(code)}
                        onMouseLeave={() => setHoveredState(null)}
                        data-testid={`state-tile-${code}`}
                        data-lit={lit ? "true" : "false"}
                      >
                        <span
                          className={`font-heading text-xs font-bold ${
                            lit ? "text-tbc-navy" : "text-white/60"
                          }`}
                        >
                          {code}
                        </span>
                        {isHovered && (
                          <div className="absolute -top-9 left-1/2 -translate-x-1/2 px-2 py-1 bg-white text-tbc-navy text-[10px] rounded whitespace-nowrap shadow-lg font-body z-20">
                            {lit ? `${count} ${count === 1 ? "business" : "businesses"}` : "Browse →"}
                          </div>
                        )}
                      </div>
                    </Link>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
